import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Settings, 
  MessageSquare, 
  Save, 
  Send, 
  CheckCircle2, 
  XCircle,
  Eye,
  EyeOff,
  RefreshCw,
  Users,
  AlertTriangle,
  Lock
} from 'lucide-react';
import { toast } from 'sonner';
import api, { authAPI } from '@/lib/api';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export default function AdminSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  
  // Telegram settings
  const [telegramSettings, setTelegramSettings] = useState({
    configured: false,
    enabled: false,
    bot_token: '',
    chat_id: '',
    bot_token_masked: ''
  });
  const [showToken, setShowToken] = useState(false);
  const [newBotToken, setNewBotToken] = useState('');
  const [newChatId, setNewChatId] = useState('');
  const [telegramEnabled, setTelegramEnabled] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await api.get('/admin/telegram/settings');
      setTelegramSettings(response.data);
      setNewChatId(response.data.chat_id || '');
      setTelegramEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
      toast.error('Fehler beim Laden der Einstellungen');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveTelegram = async () => {
    setSaving(true);
    try {
      const payload = {
        bot_token: newBotToken || '__KEEP_EXISTING__',
        chat_id: newChatId,
        enabled: telegramEnabled
      };
      
      await api.post('/admin/telegram/settings', payload);
      toast.success('Telegram-Einstellungen gespeichert');
      setNewBotToken(''); // Clear token input after save
      fetchSettings(); // Refresh to get masked token
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTelegram = async () => {
    setTesting(true);
    try {
      const response = await api.post('/admin/telegram/test');
      if (response.data.status === 'success') {
        toast.success('Test-Nachricht erfolgreich gesendet!');
      } else {
        toast.error(`Test fehlgeschlagen: ${response.data.reason || response.data.error || 'Unbekannter Fehler'}`);
      }
    } catch (error) {
      console.error('Telegram test failed:', error);
      toast.error('Test fehlgeschlagen: ' + (error.response?.data?.detail || 'Verbindungsfehler'));
    } finally {
      setTesting(false);
    }
  };

  const handleMigratePasswords = async () => {
    setMigrating(true);
    setShowMigrateDialog(false);
    try {
      const response = await api.post('/admin/migrate-customer-passwords');
      const { migrated, failed, message } = response.data;
      
      if (migrated > 0) {
        toast.success(`${message}. ${failed > 0 ? `${failed} Fehler.` : ''}`);
      } else {
        toast.info(message);
      }
    } catch (error) {
      console.error('Migration failed:', error);
      toast.error('Migration fehlgeschlagen: ' + (error.response?.data?.detail || 'Unbekannter Fehler'));
    } finally {
      setMigrating(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwörter stimmen nicht überein');
      return;
    }

    if (newPassword.length < 8) {
      toast.error('Passwort muss mindestens 8 Zeichen lang sein');
      return;
    }

    setSavingPassword(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Passwort erfolgreich geändert');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Fehler beim Ändern des Passworts');
    } finally {
      setSavingPassword(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#9B1B30]" />
      </div>
    );
  }

  return (
    <div data-testid="admin-settings">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-barlow font-bold text-2xl sm:text-3xl text-[#1A1A1A] mb-2">
          <Settings className="inline-block w-7 h-7 mr-2 text-[#9B1B30]" />
          Einstellungen
        </h1>
        <p className="font-manrope text-sm text-gray-600">
          System- und Benachrichtigungseinstellungen verwalten
        </p>
      </div>

      {/* Telegram Settings */}
      <Card className="mb-6" data-testid="telegram-settings-card">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-barlow text-lg flex items-center gap-2">
                <MessageSquare className="w-5 h-5 text-blue-500" />
                Telegram-Benachrichtigungen
              </CardTitle>
              <CardDescription className="mt-1">
                Erhalten Sie Benachrichtigungen über neue Termine und Chat-Nachrichten via Telegram
              </CardDescription>
            </div>
            <Badge 
              variant={telegramSettings.configured ? "default" : "secondary"}
              className={telegramSettings.configured ? "bg-green-500" : ""}
            >
              {telegramSettings.configured ? (
                <><CheckCircle2 className="w-3 h-3 mr-1" /> Konfiguriert</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Nicht konfiguriert</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="font-barlow font-bold">Telegram aktivieren</Label>
              <p className="text-sm text-gray-500 mt-0.5">Benachrichtigungen ein- oder ausschalten</p>
            </div>
            <Switch
              checked={telegramEnabled}
              onCheckedChange={setTelegramEnabled}
              data-testid="telegram-enabled-switch"
            />
          </div>

          {/* Bot Token */}
          <div className="space-y-2">
            <Label className="font-barlow font-bold">Bot Token</Label>
            {telegramSettings.configured && !newBotToken && (
              <div className="flex items-center gap-2 p-3 bg-gray-100 rounded-md">
                <code className="text-sm text-gray-600">
                  {showToken ? telegramSettings.bot_token_masked : '••••••••••••••••••••'}
                </code>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="h-7 w-7 p-0"
                  onClick={() => setShowToken(!showToken)}
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
            )}
            <Input
              type="password"
              placeholder={telegramSettings.configured ? "Neuen Token eingeben (leer lassen für bestehenden)" : "Bot Token eingeben"}
              value={newBotToken}
              onChange={(e) => setNewBotToken(e.target.value)}
              data-testid="telegram-bot-token-input"
            />
            <p className="text-xs text-gray-500">
              Erstellen Sie einen Bot bei @BotFather auf Telegram und kopieren Sie den Token hierher.
            </p>
          </div>

          {/* Chat ID */}
          <div className="space-y-2">
            <Label className="font-barlow font-bold">Chat ID</Label>
            <Input
              type="text"
              placeholder="Chat ID eingeben"
              value={newChatId}
              onChange={(e) => setNewChatId(e.target.value)}
              data-testid="telegram-chat-id-input"
            />
            <p className="text-xs text-gray-500">
              Ihre persönliche Chat-ID. Senden Sie "/start" an @userinfobot um Ihre ID zu erfahren.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button 
              onClick={handleSaveTelegram}
              disabled={saving || (!newBotToken && !telegramSettings.configured)}
              className="bg-[#9B1B30] hover:bg-[#7A1525]"
              data-testid="save-telegram-btn"
            >
              {saving ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Speichern
            </Button>
            
            <Button 
              variant="outline"
              onClick={handleTestTelegram}
              disabled={testing || !telegramSettings.configured}
              data-testid="test-telegram-btn"
            >
              {testing ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              Test-Nachricht senden
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customer Migration */}
      <Card data-testid="migration-card">
        <CardHeader>
          <CardTitle className="font-barlow text-lg flex items-center gap-2">
            <Users className="w-5 h-5 text-amber-500" />
            Kunden-Migration
          </CardTitle>
          <CardDescription>
            Beheben Sie Kunden ohne gültiges Passwort durch automatische Migration
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-bold text-amber-800">Achtung</p>
                <p className="text-sm text-amber-700 mt-1">
                  Diese Funktion generiert neue Passwörter für alle Kunden ohne gültiges Passwort 
                  und sendet ihnen eine E-Mail mit dem neuen Passwort. Dies kann nicht rückgängig gemacht werden.
                </p>
              </div>
            </div>
          </div>
          
          <Button 
            variant="outline"
            onClick={() => setShowMigrateDialog(true)}
            disabled={migrating}
            className="border-amber-500 text-amber-700 hover:bg-amber-50"
            data-testid="migrate-passwords-btn"
          >
            {migrating ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Users className="w-4 h-4 mr-2" />
            )}
            Kunden-Passwörter migrieren
          </Button>
        </CardContent>
      </Card>

      {/* Migration Confirmation Dialog */}
      <AlertDialog open={showMigrateDialog} onOpenChange={setShowMigrateDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Kunden-Passwörter migrieren?</AlertDialogTitle>
            <AlertDialogDescription>
              Diese Aktion wird:
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Alle Kunden ohne gültiges Passwort finden</li>
                <li>Für jeden ein neues sicheres Passwort generieren</li>
                <li>Das Passwort per E-Mail an den Kunden senden</li>
              </ul>
              <p className="mt-3 font-medium text-amber-600">
                Diese Aktion kann nicht rückgängig gemacht werden!
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleMigratePasswords}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Migration starten
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Change Password Card */}
      <Card className="mt-6" data-testid="password-change-card">
        <CardHeader>
          <CardTitle className="font-barlow text-xl flex items-center gap-2">
            <Lock className="w-5 h-5 text-[#2E2A88]" />
            Passwort ändern
          </CardTitle>
          <CardDescription>
            Ändern Sie hier Ihr Admin-Passwort
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <Label className="font-barlow font-bold text-sm">Aktuelles Passwort</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                className="mt-1"
                required
                data-testid="admin-current-password"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Neues Passwort</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="mt-1"
                placeholder="Min. 8 Zeichen"
                required
                data-testid="admin-new-password"
              />
            </div>
            <div>
              <Label className="font-barlow font-bold text-sm">Neues Passwort bestätigen</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="mt-1"
                required
                data-testid="admin-confirm-password"
              />
            </div>
            <Button 
              type="submit" 
              className="btn-primary"
              disabled={savingPassword}
              data-testid="admin-save-password-btn"
            >
              {savingPassword ? 'Wird gespeichert...' : 'Passwort ändern'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
